/**
 * /login — identity sign-in (email + password).
 *
 * Mirrors `frontend/src/app/(auth)/login/page.tsx` (the Next.js
 * original). Three post-submit branches:
 *   1. `select_workspace:true` — the identity has >=2 active
 *      memberships. The backend has set an identity-only session
 *      cookie. We open the picker; selecting a workspace POSTs
 *      /auth/activate.
 *   2. `totp_required:true` — TOTP already enrolled; the access cookie
 *      is totp=false. Route to /2fa-verify before any TOTP-gated page.
 *   3. M-1 `totp_enrollment_required` 403 — a mandatory-2FA role
 *      (super_admin / hr_manager) hasn't enrolled. The backend set an
 *      enrolment-scoped identity-only session; force /2fa-setup.
 *   4. Otherwise — single-membership / platform identity. Honour
 *      `?next=` (sanitised via safeNext) or fall back to /dashboard.
 *
 * Migration note: 1:1 port of the Next.js page. Replacements:
 *   - next/link → @tanstack/react-router <Link to=…>
 *   - useRouter().replace() → useNavigate({ to, replace: true })
 *   - useSearchParams() → Route.useSearch() (typed, Zod-validated)
 *   - <Suspense> wrapper (Next.js client-component pattern) removed
 *     — Vite has no SSR boundary, so the inner form renders directly.
 */

import { useEffect, useRef, useState } from "react";
import {
  Link,
  createFileRoute,
  useNavigate,
} from "@tanstack/react-router";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { z } from "zod";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth, safeNext } from "@/lib/auth";
import { postAuthRedirect } from "@/lib/auth/safe-next";
import { loginSchema, type LoginValues } from "@/lib/auth/schemas";
import { handleAuthError } from "@/lib/auth/form-errors";
import { toApiError, isTotpEnrollmentRequired } from "@/lib/api";
import {
  isSelectWorkspace,
  type LoginResponse,
  type WorkspaceChoice,
} from "@/types/auth";
import { WorkspacePickerDialog } from "@/components/auth/workspace-picker-dialog";

const FIELDS = ["email", "password"] as const;

// Search-param schema. TanStack Router will validate every nav into
// /login against this — invalid shapes are stripped, not coerced into
// runtime bugs. `next` is still passed through safeNext() at use sites
// (defence in depth: we never trust the URL as the SOLE guard).
const loginSearchSchema = z.object({
  next: z.string().optional(),
  reason: z.enum(["session_revoked"]).optional(),
});

export const Route = createFileRoute("/_auth/login")({
  validateSearch: loginSearchSchema,
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { next: nextRaw, reason } = Route.useSearch();
  const { login, activate, refresh } = useAuth();
  const [submitting, setSubmitting] = useState(false);

  // Multi-company picker: populated when /auth/login returned
  // select_workspace:true. The backend already set an IDENTITY-ONLY
  // session cookie; picking a workspace POSTs to /auth/activate.
  const [picker, setPicker] = useState<{
    workspaces: WorkspaceChoice[];
    totp: boolean;
  } | null>(null);

  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  // safeNext() rejects off-origin / protocol-relative targets so a
  // crafted ?next=https://evil… cannot bounce the user off-site.
  const nextPath = safeNext(nextRaw);

  // The api client redirects here with ?reason=session_revoked after
  // the backend reported refresh_reuse (token theft → all sessions
  // revoked). Surface a neutral notice — no specifics leaked.
  const sessionRevoked = reason === "session_revoked";
  const revokedToastShown = useRef(false);
  useEffect(() => {
    if (sessionRevoked && !revokedToastShown.current) {
      revokedToastShown.current = true;
      toast.error(
        "Your sessions were ended for security. Please sign in again.",
      );
    }
  }, [sessionRevoked]);

  // M-1 mandatory-2FA gate: a super_admin / hr_manager identity that
  // hasn't enrolled. The backend already 403'd and set an
  // enrolment-scoped identity-only session — send the user to
  // /2fa-setup (required mode).
  function routeToMandatory2fa() {
    void navigate({
      to: "/2fa-setup",
      search: { required: 1, next: nextPath },
      replace: true,
    });
  }

  async function routeAfterSession(res: LoginResponse) {
    // Backend signals an existing TOTP enrolment: the access cookie was
    // issued with totp=false, so step up before any TOTP-gated route.
    if (res.totp_required) {
      void navigate({
        to: "/2fa-verify",
        search: { next: nextPath },
        replace: true,
      });
      return;
    }
    // Re-fetch /me so we have the freshly-activated tenant's slug —
    // useAuth.login() already called refresh() internally, but its
    // setState hasn't propagated to this closure yet. Calling refresh
    // again is cheap (one /me round trip) and gives us the slug
    // synchronously via its return value.
    const me = await refresh();
    const target = postAuthRedirect(nextPath, me?.tenant?.slug);
    void navigate({ to: target, replace: true });
  }

  async function onSubmit(values: LoginValues) {
    setSubmitting(true);
    try {
      const res = await login({
        email: values.email,
        password: values.password,
      });
      // >=2 memberships → identity-only session was set; open the
      // picker. /auth/activate completes the pick with no re-auth.
      if (isSelectWorkspace(res)) {
        setPicker({
          workspaces: res.workspaces,
          totp: res.totp_required,
        });
        return;
      }
      // Single membership / platform identity → full session.
      await routeAfterSession(res);
    } catch (err) {
      // M-1: 403 totp_enrollment_required — mandatory 2FA, not a
      // credential failure. The backend set the enrolment-scoped
      // session; force enrolment instead of showing a form error.
      if (isTotpEnrollmentRequired(err)) {
        routeToMandatory2fa();
        return;
      }
      handleAuthError(err, form.setError, FIELDS);
    } finally {
      setSubmitting(false);
    }
  }

  // Picker → POST /auth/activate { slug }. On success the backend
  // rotates in the full tenant session; route through the SAME
  // post-session path (honours totp_required).
  async function onPickWorkspace(slug: string) {
    if (!picker) return;
    try {
      const res = await activate(slug);
      setPicker(null);
      await routeAfterSession(res);
    } catch (err) {
      if (isTotpEnrollmentRequired(err)) {
        setPicker(null);
        routeToMandatory2fa();
        return;
      }
      toast.error(toApiError(err).message);
      throw err;
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Sign in</CardTitle>
          <CardDescription>
            Welcome back. Enter your credentials to continue.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sessionRevoked && (
            <div
              role="alert"
              className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
            >
              Your sessions were ended for security. Please sign in
              again.
            </div>
          )}
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4"
              noValidate
            >
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        autoComplete="email"
                        placeholder="you@company.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        autoComplete="current-password"
                        placeholder="••••••••••••"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                    {/* Forgot link AFTER the input so the keyboard
                        tab flow is Email → Password → Sign in →
                        Forgot. Visually right-aligned under the input
                        keeps it discoverable. */}
                    <div className="flex justify-end">
                      <Link
                        to="/forgot"
                        className="text-xs font-medium text-primary hover:underline"
                      >
                        Forgot password?
                      </Link>
                    </div>
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                className="w-full"
                disabled={submitting}
              >
                {submitting && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Sign in
              </Button>
            </form>
          </Form>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            No account?{" "}
            <Link
              to="/register"
              className="font-medium text-primary hover:underline"
            >
              Create a workspace
            </Link>
          </p>
        </CardContent>
      </Card>

      <WorkspacePickerDialog
        open={picker !== null}
        workspaces={picker?.workspaces ?? []}
        totpRequired={picker?.totp ?? false}
        onCancel={() => setPicker(null)}
        onSelect={onPickWorkspace}
      />
    </>
  );
}
